<template>
  <BaseModal
    v-model="isOpen"
    class="truck-series-modal"
    :clickOutsideClose="false"
  >
    <FileSelector
      v-if="imageFor"
      v-model="showFileSelector"
      type="assets"
      subcategory="truck_image"
      @fileUploaded="onFile"
      @selectedFile="onFile"
    />

    <template v-slot:header>
      <h3 class="text-lg font-semibold text-gray-900">Series de camiones</h3>
    </template>

    <p class="text-sm text-gray-600 mb-4">
      Agrupan los modelos en el menú y en la portada del sitio. Ocultar una
      serie oculta sus camiones; borrarla los deja "sin serie".
    </p>

    <div class="space-y-3">
      <div
        v-for="serie in rows"
        :key="serie.key"
        class="flex items-start space-x-3 border border-gray-200 rounded-lg p-3"
      >
        <button
          class="w-24 h-16 bg-gray-100 rounded flex-shrink-0 overflow-hidden"
          title="Cambiar imagen"
          @click="pickImage(serie)"
        >
          <img
            v-if="serie.imageUrl"
            :src="assetUrl(serie.imageUrl)"
            class="w-full h-full object-contain"
          />
          <i v-else class="mdi mdi-image-plus text-2xl text-gray-400"></i>
        </button>
        <div class="flex-1 grid grid-cols-1 md:grid-cols-4 gap-2">
          <input
            v-model="serie.name"
            type="text"
            class="field md:col-span-2"
            placeholder="Nombre"
          />
          <input
            v-model.number="serie.sortOrder"
            type="number"
            class="field"
            title="Orden"
          />
          <label class="inline-flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              :checked="serie.status == '1'"
              @change="serie.status = $event.target.checked ? '1' : '0'"
            />
            <span>Visible</span>
          </label>
          <textarea
            v-model="serie.description"
            rows="2"
            class="field md:col-span-4"
            placeholder="Descripción corta"
          ></textarea>
        </div>
        <div class="flex flex-col space-y-2">
          <button
            class="px-3 py-1 text-sm text-white bg-red-600 rounded disabled:opacity-40"
            :disabled="saving || !changed(serie)"
            @click="save(serie)"
          >
            Guardar
          </button>
          <button
            v-if="serie.id"
            class="px-3 py-1 text-sm text-gray-600"
            :disabled="saving"
            @click="remove(serie)"
          >
            Borrar
          </button>
        </div>
      </div>
    </div>

    <button class="mt-4 text-sm text-blue-600" @click="addSeries()">
      + Nueva serie
    </button>
  </BaseModal>
</template>

<script>
import FileSelector from "@/components/FileSelector.vue";
import {
  saveSeries,
  deleteSeries,
  truckAssetUrl
} from "@/api/store/TrucksService.js";

const FIELDS = ["name", "description", "imageUrl", "sortOrder", "status"];

export default {
  name: "TruckSeriesModal",
  components: { FileSelector },
  props: {
    series: { type: Array, default: () => [] }
  },
  data() {
    return {
      isOpen: true,
      rows: this.series.map(serie => this.toRow(serie)),
      saving: false,
      showFileSelector: false,
      imageFor: null
    };
  },
  watch: {
    isOpen(val) {
      if (!val) this.$emit("close");
    },
    showFileSelector(val) {
      if (!val) this.imageFor = null;
    }
  },
  methods: {
    assetUrl: truckAssetUrl,
    toRow(serie) {
      const row = {
        id: serie.id || null,
        key: serie.id || `new-${Date.now()}`,
        name: serie.name || "",
        slug: serie.slug || "",
        description: serie.description || "",
        imageUrl: serie.imageUrl || null,
        sortOrder: Number(serie.sortOrder) || 0,
        status: serie.status == null ? "1" : String(serie.status)
      };
      row.saved = JSON.stringify(FIELDS.map(f => row[f]));
      return row;
    },
    changed(row) {
      return JSON.stringify(FIELDS.map(f => row[f])) !== row.saved;
    },
    addSeries() {
      this.rows.push(this.toRow({ sortOrder: this.rows.length }));
    },
    pickImage(row) {
      this.imageFor = row;
      this.showFileSelector = true;
    },
    onFile(file) {
      const url = file.s3Url || file.url;
      if (url && this.imageFor) this.imageFor.imageUrl = url;
      this.showFileSelector = false;
    },
    async save(row) {
      if (!row.name.trim()) {
        this.notify("El nombre es obligatorio", "error");
        return;
      }
      this.saving = true;
      try {
        const res = await saveSeries(row);
        if (res.status !== "success") throw new Error(res.message);
        const index = this.rows.indexOf(row);
        this.$set(this.rows, index, this.toRow(res.series));
        this.$emit("changed");
        this.notify("Serie guardada", "success");
      } catch (e) {
        this.notify(
          e.response?.data?.message || e.message || "Error al guardar",
          "error"
        );
      } finally {
        this.saving = false;
      }
    },
    async remove(row) {
      if (
        !confirm(`¿Borrar la serie ${row.name}? Sus camiones quedan sin serie.`)
      )
        return;
      this.saving = true;
      try {
        await deleteSeries(row.id);
        this.rows = this.rows.filter(r => r !== row);
        this.$emit("changed");
      } catch (e) {
        this.notify("No se pudo borrar", "error");
      } finally {
        this.saving = false;
      }
    },
    notify(message, type) {
      this.$toast.open({ message, type, position: "top-right" });
    }
  }
};
</script>

<style lang="scss" scoped>
.field {
  @apply w-full px-3 py-2 border border-gray-300 rounded-lg text-sm;
}
:deep(.modal-content) {
  max-width: 900px;
}
</style>
